import { useCallback, useEffect, useState } from 'react';
import { CheckCircle, AlertTriangle, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Segmented } from '@/components/ui/segmented';
import { formatDate } from '@/lib/formatDate';
import {
  platformAdminGetStrengthFixes, platformAdminApplyStrengthFix, platformAdminRejectStrengthFix,
  type StrengthFix,
} from '@/lib/api';

type Status = StrengthFix['review_status'];

// Identical corrections from several clinics are reviewed as one group
type FixGroup = { key: string; rep: StrengthFix; clinics: StrengthFix[]; latest: string };

const num = (v: string | number | null | undefined) => (v == null || v === '' ? null : Number(v));
const fmt = (v: number) => String(Number(v.toFixed(4)));

function strengthLabel(value: number | null, unit: string | null, perVolume: number | null): string {
  if (value == null) return '—';
  return `${fmt(value)} ${unit ?? ''}${perVolume != null ? ` / ${fmt(perVolume)} ml` : ''}`.trim();
}

// Strength after the correction (unset proposal fields keep the catalog value)
function proposed(f: StrengthFix) {
  return {
    value: num(f.dose_value) ?? num(f.catalog_dose_value),
    unit: f.dose_unit ?? f.catalog_dose_unit,
    perVolume: num(f.per_volume_value) ?? num(f.catalog_per_volume_value),
  };
}

// "Adds volume" is the common, low-risk case; a changed amount or unit needs a closer look
function changeKind(f: StrengthFix): 'adds-volume' | 'changes-amount' | 'changes-volume' {
  const p = proposed(f);
  if (p.value !== num(f.catalog_dose_value) || (p.unit ?? '') !== (f.catalog_dose_unit ?? '')) return 'changes-amount';
  if (num(f.catalog_per_volume_value) == null && p.perVolume != null) return 'adds-volume';
  return 'changes-volume';
}

// Compare with the strength printed in the catalog's composition text, e.g. "Paracetamol (250mg/5ml)"
function labelCheck(f: StrengthFix): 'matches' | 'differs' | 'unknown' {
  const text = f.composition_raw ?? '';
  const p = proposed(f);
  const re = /(\d+(?:\.\d+)?)\s*(mg|mcg|µg|g|iu|meq)\b(?:\s*\/\s*(\d+(?:\.\d+)?)?\s*ml)?/gi;
  const found = [...text.matchAll(re)].map(m => ({
    value: Number(m[1]),
    unit: m[2].toLowerCase().replace('µg', 'mcg'),
    perVolume: m[0].toLowerCase().includes('ml') ? Number(m[3] ?? 1) : null,
  }));
  if (!found.length || p.value == null) return 'unknown';
  const unit = (p.unit ?? '').toLowerCase();
  const sameAmount = found.filter(x => x.value === p.value && x.unit === unit);
  if (!sameAmount.length) return 'differs';
  if (p.perVolume == null) return 'matches';
  const withVolume = sameAmount.filter(x => x.perVolume != null);
  if (!withVolume.length) return 'unknown';
  return withVolume.some(x => x.perVolume === p.perVolume) ? 'matches' : 'differs';
}

function groupFixes(fixes: StrengthFix[]): FixGroup[] {
  const groups = new Map<string, FixGroup>();
  for (const f of fixes) {
    const key = [f.drug_id, f.molecule_id, num(f.dose_value), f.dose_unit, num(f.per_volume_value), f.review_status].join('|');
    const g = groups.get(key);
    if (g) {
      g.clinics.push(f);
      if (f.updated_at > g.latest) g.latest = f.updated_at;
    } else {
      groups.set(key, { key, rep: f, clinics: [f], latest: f.updated_at });
    }
  }
  return [...groups.values()].sort((a, b) => b.clinics.length - a.clinics.length || b.latest.localeCompare(a.latest));
}

const CHANGE_LABEL = {
  'adds-volume':    { text: 'Adds volume',    cls: 'bg-muted text-muted-foreground' },
  'changes-volume': { text: 'Changes volume', cls: 'bg-highlight text-highlight-foreground' },
  'changes-amount': { text: 'Changes amount', cls: 'bg-highlight text-highlight-foreground' },
} as const;

export default function StrengthFixesPanel({ ok, err, onPendingCount }: {
  ok: (msg: string) => void;
  err: (msg: string) => void;
  onPendingCount?: (n: number) => void;
}) {
  const [status, setStatus] = useState<Status>('pending');
  const [fixes, setFixes] = useState<StrengthFix[] | null>(null);
  const [confirm, setConfirm] = useState<{ group: FixGroup; action: 'apply' | 'reject' } | null>(null);
  const [removeFromClinics, setRemoveFromClinics] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setFixes(null);
    try {
      const rows = (await platformAdminGetStrengthFixes(status)) ?? [];
      setFixes(rows);
      if (status === 'pending') onPendingCount?.(groupFixes(rows).length);
    } catch (e: any) {
      setFixes([]);
      err(e?.message || 'Failed to load corrections');
    }
  }, [status]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const groups = groupFixes(fixes ?? []);

  const runAction = async () => {
    if (!confirm) return;
    const { group, action } = confirm;
    setSaving(true);
    try {
      if (action === 'apply') {
        await platformAdminApplyStrengthFix(group.rep.id);
        ok(`${group.rep.brand_name} updated in the catalog`);
      } else {
        await platformAdminRejectStrengthFix(group.rep.id, removeFromClinics);
        ok(removeFromClinics ? 'Rejected and removed from the clinics' : 'Rejected — clinics keep their correction');
      }
      setConfirm(null);
      load();
    } catch (e: any) {
      err(e?.message || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-muted-foreground">
          Strength corrections clinics saved in the dose calculator. They already apply to that clinic;
          applying copies them into the shared catalog for everyone.
        </p>
        <Segmented<Status> aria-label="Review status" value={status} onChange={setStatus}
          options={[{ value: 'pending', label: 'Pending' }, { value: 'applied', label: 'Applied' }, { value: 'rejected', label: 'Rejected' }]} />
      </div>

      {fixes === null ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          {status === 'pending' ? 'No corrections waiting for review.' : `No ${status} corrections.`}
        </div>
      ) : (
        // A list, not a wide table: the actions must stay visible at any width
        <ul className="rounded-md border divide-y">
          {groups.map(g => {
            const f = g.rep;
            const p = proposed(f);
            const kind = changeKind(f);
            const label = labelCheck(f);
            const removed = f.dose_value == null && f.per_volume_value == null;
            return (
              <li key={g.key} className="px-3 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium">{f.brand_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {f.molecule_name}{f.form ? ` · ${f.form}` : ''}{f.drug_tenant_id ? ' · clinic-private medicine' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-mono text-muted-foreground">
                      {strengthLabel(num(f.catalog_dose_value), f.catalog_dose_unit, num(f.catalog_per_volume_value))}
                    </span>
                    <span className="text-muted-foreground" aria-hidden="true">→</span>
                    {removed ? (
                      <span className="text-muted-foreground">removed from clinics</span>
                    ) : (<>
                      <span className="font-mono font-semibold">{strengthLabel(p.value, p.unit, p.perVolume)}</span>
                      <span className={cn('px-1.5 py-0.5 rounded text-2xs', CHANGE_LABEL[kind].cls)}>{CHANGE_LABEL[kind].text}</span>
                    </>)}
                  </div>
                  <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-muted-foreground">
                    {!removed && label === 'matches' && <span className="flex items-center gap-1 text-success"><CheckCircle className="w-3.5 h-3.5" />Matches label text</span>}
                    {!removed && label === 'differs' && <span className="flex items-center gap-1 text-warning"><AlertTriangle className="w-3.5 h-3.5" />Differs from label text</span>}
                    {!removed && label === 'unknown' && <span>Label text has no volume</span>}
                    {f.composition_raw && <span className="truncate max-w-[260px]" title={f.composition_raw}>“{f.composition_raw}”</span>}
                    <span className="flex items-center gap-1"
                      title={g.clinics.map(c => `${c.tenant_name}${c.created_by_name ? ` (${c.created_by_name})` : ''}`).join('\n')}>
                      <Building2 className="w-3.5 h-3.5" />
                      {g.clinics.length === 1 ? g.clinics[0].tenant_name : `${g.clinics.length} clinics`} · {formatDate(g.latest)}
                    </span>
                  </div>
                </div>
                {status === 'pending' && (
                  <div className="flex items-center gap-1 shrink-0 sm:justify-end">
                    <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground"
                      onClick={() => { setRemoveFromClinics(true); setConfirm({ group: g, action: 'reject' }); }}>Reject</Button>
                    <Button size="sm" className="h-8 text-xs" onClick={() => setConfirm({ group: g, action: 'apply' })}>Apply to catalog</Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={!!confirm} onOpenChange={o => { if (!o) setConfirm(null); }}>
        <DialogContent className="max-w-md">
          {confirm && (() => {
            const f = confirm.group.rep;
            const p = proposed(f);
            const before = strengthLabel(num(f.catalog_dose_value), f.catalog_dose_unit, num(f.catalog_per_volume_value));
            const after = strengthLabel(p.value, p.unit, p.perVolume);
            const clinicCount = confirm.group.clinics.length;
            return (<>
              <DialogHeader>
                <DialogTitle>{confirm.action === 'apply' ? 'Apply to catalog' : 'Reject correction'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p><span className="font-medium">{f.brand_name}</span> · {f.molecule_name}</p>
                <p className="font-mono text-xs">{before} → {after}</p>
                {confirm.action === 'apply' ? (
                  <p className="text-xs text-muted-foreground">
                    Every clinic using this medicine will calculate doses with the new strength.
                    {clinicCount > 1 && ` ${clinicCount} clinics submitted this correction.`}
                  </p>
                ) : (
                  <label className="flex items-start gap-2 text-xs">
                    <Checkbox checked={removeFromClinics} onCheckedChange={c => setRemoveFromClinics(c === true)} className="mt-0.5" />
                    <span>
                      Also stop {clinicCount > 1 ? `the ${clinicCount} clinics` : 'the clinic'} using it
                      <span className="block text-muted-foreground">Their calculator goes back to the catalog strength and asks for the volume again. Their bottle size is kept.</span>
                    </span>
                  </label>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" size="sm" onClick={() => setConfirm(null)}>Cancel</Button>
                <Button size="sm" variant={confirm.action === 'reject' ? 'destructive' : 'default'} disabled={saving} onClick={runAction}>
                  {saving ? 'Saving…' : confirm.action === 'apply' ? 'Apply to catalog' : 'Reject'}
                </Button>
              </DialogFooter>
            </>);
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
